import 'swiper/css';
import { graphql } from 'gatsby';
import parse from 'html-react-parser';
import {
  Header,
  Wrapper,
  Eyebrow,
  Headline,
  Subheadline,
  Section,
  Container,
} from './CarouselSection.styles';
import { applyPaddingCSS } from '@/utils/generatePadding';
import type { BlockHTMLId, BlockHTMLClassName } from '@/utils/renderBlock';
import { lazy, Suspense } from 'react';
const ContentCarousel = lazy(() => import('./components/ContentCarousel/ContentCarousel'));
const ImageCarousel = lazy(() => import('./components/ImageCarousel/ImageCarousel'));
const TabbedCarousel = lazy(() => import('./components/TabbedCarousel/TabbedCarousel'));
const AwardsCarousel = lazy(() => import('./components/AwardsCarousel/AwardsCarousel'));

const CarouselSection = ({
  carouselSectionElements: { eyebrow, headline, subheadline, carousels, ...props },
  system,
  id,
  className,
}: Queries.CAROUSEL_SECTIONFragment & BlockHTMLId & BlockHTMLClassName) => {
  const align = props.align?.value ?? 'center';

  return (
    <Section
      data-codename={system.codename}
      align={align}
      css={applyPaddingCSS(props)}
      id={id}
      className={className}
    >
      <Container>
        <Header>
          {eyebrow?.value && <Eyebrow>{eyebrow.value}</Eyebrow>}
          <Headline>{parse(headline.value.replace(/\./g, '. <br />'))}</Headline>
          {subheadline?.value && <Subheadline>{subheadline.value}</Subheadline>}
        </Header>
      </Container>
      {carousels.value.map((carousel) => (
        <Wrapper key={carousel.system.id}>
          <Suspense>
            {carousel.__typename === 'kontent_item_content_carousel' && (
              <ContentCarousel {...carousel} />
            )}
            {carousel.__typename === 'kontent_item_image_carousel' && (
              <ImageCarousel {...carousel} />
            )}
            {carousel.__typename === 'kontent_item_tabbed_carousel' && (
              <TabbedCarousel {...carousel} system={system} />
            )}
            {carousel.__typename === 'kontent_item_awards_carousel' && (
              <AwardsCarousel {...carousel} system={system} />
            )}
          </Suspense>
        </Wrapper>
      ))}
    </Section>
  );
};

export const fragment = graphql`
  fragment CAROUSEL_SECTION on kontent_item_carousel_section {
    __typename
    system {
      codename
      id
    }
    carouselSectionElements: elements {
      ...COMPONENT_A_B_TEST
      ...COMPONENT_PERSONALIZATION
      ...PADDING
      eyebrow {
        value
      }
      headline {
        value
      }
      subheadline {
        value
      }
      align {
        value
      }
      carousels {
        value {
          __typename
          ...CONTENT_CAROUSEL
          ...IMAGE_CAROUSEL
          ...TABBED_CAROUSEL
          ...AWARDS_CAROUSEL
        }
      }
    }
  }
`;

export default CarouselSection;
